import React from 'react';

import { Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import Joyride, { Step } from 'react-joyride';

import {
  getActiveDashboard,
  DashboardType,
  TypeOfUser,
  getTypeOfUser,
  startTour,
  pharmacyBillingSelector,
} from '@pharmaplan/common';

import AdsContainer from '../common/AdsContainer';
import ContentContainer from '../common/ContentContainer';
import LegendContainer from '../common/LegendContainer';

import { useAppSelector } from '../../hooks/useAppSelector';
import useUser from '../../hooks/useUser';
import useGuidelines from '../../hooks/useGuidelines';
import useDashboard from '../../hooks/useDashboard';
import useForegroundNotifications from '../../hooks/PushNotifications/useForegroundNotifications';
import strings from '../../localization';
import ResponsiveClasses from '../../theme/ResponsiveClasses';
import {
  journeyStepsPharmacist,
  journeyStepsPharmacy,
} from '../../helpers/journeySteps';
import { BreakPoints } from '../../helpers/Constants';

import useProfessional from '../../hooks/Professional/useProfessional';
import GuideTooltip from '../GuideTooltip/GuideTooltip';
import CustomDrawer from '../Drawer/Drawer';

import MainCalendar from './MainCalendar';
import PharmacistMap from './PharmacistMap';
import Toolbar from './Toolbar';
import classes from './style';
import PromptPlanRibbon from './PromptPlanRibbon';

export const DashboardComponents = {
  [DashboardType.calendar]: <MainCalendar />,
  [DashboardType.map]: <PharmacistMap />,
};

const { pharmacy } = TypeOfUser;
const { lg } = BreakPoints;

const Dashboard = () => {
  const theme = useTheme();
  const { name } = useUser();

  const lessThanLg = useMediaQuery(theme.breakpoints.down(lg));
  const { runTour, tourRef, handleJoyrideCallback } = useGuidelines();

  const activeDash = useAppSelector(getActiveDashboard);
  const typeOfUser = useAppSelector(getTypeOfUser);
  const canStartTour = useAppSelector(startTour);

  const { tour } = runTour;

  const billing = useAppSelector(pharmacyBillingSelector);

  const { creditCardLast4, basicFees } = billing ?? {};
  const { isProfessional } = useProfessional();
  const isPharmacy = typeOfUser === pharmacy;

  const showRibbon = !!basicFees && !creditCardLast4 && isPharmacy;

  // Using session storage here to check if user skipped the guideline and not to display it again until user reopens the app
  const isTempSkip = localStorage.getItem('tempSkip');
  const guidelineSteps = isProfessional
    ? journeyStepsPharmacist()
    : journeyStepsPharmacy();

  const checkCanStartTour = tour && !isTempSkip;
  const initTour = checkCanStartTour || canStartTour;

  const toolbarTitle = strings.formatString(
    strings.userDashboard,
    name ?? '',
  ) as string;

  useDashboard();
  useForegroundNotifications();

  return (
    <Grid container spacing={1}>
      {/* Guideline */}
      <Joyride
        steps={guidelineSteps as Step[]}
        run={initTour}
        callback={handleJoyrideCallback}
        continuous
        ref={tourRef}
        tooltipComponent={GuideTooltip}
      />

      {showRibbon && <PromptPlanRibbon />}

      {/* main calendar */}
      <Grid item xs={12} lg={10}>
        <CustomDrawer />
        {lessThanLg && <LegendContainer row key={2} />}
        <ContentContainer>
          <Toolbar title={toolbarTitle} />
          {DashboardComponents[activeDash]}
        </ContentContainer>
      </Grid>

      {/* ads and legend */}
      <Grid sx={ResponsiveClasses.hideOnLg} item xs={2}>
        <Grid item sx={classes.legendGrid}>
          <LegendContainer key={1} />
        </Grid>
        <Grid item>
          <AdsContainer />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Dashboard;
